import {
  Avatar,
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  Notice,
  Panel,
  SegmentedControl,
  Skeleton,
  Tabs,
  THEME_NAMES,
  type ThemeChoice,
  useTheme,
} from '@mogzo/ui';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { NavLink, useParams } from 'react-router';
import { Page } from '../components/Page';
import { authClient, authErrorMessage, PLAYER_SITE } from '../lib/api';
import { useDeveloper } from '../lib/developer';
import { formatDate } from '../lib/format';

const ROLE_NAMES: Record<string, string> = {
  player: 'Player',
  developer: 'Developer',
  moderator: 'Moderator',
  reviewer: 'Reviewer',
  super_admin: 'Super admin',
};

function Profile() {
  const me = useDeveloper();
  // The display name is always a copy of the username.
  const rows = [
    ['Username', me.displayName],
    ['Email', me.email],
    ['Account type', me.roles.map((role) => ROLE_NAMES[role] ?? role).join(', ')],
  ];
  return (
    <Panel
      title="Profile"
      description={
        <>
          Your username and email are the same on mogzo.com, this dashboard and the CLI.{' '}
          <a href={`${PLAYER_SITE}/account/edit`}>Change them on mogzo.com</a>.
        </>
      }
    >
      <dl className="m-0 grid gap-4 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[13px] text-muted">{label}</dt>
            <dd className="m-0 mt-0.5 break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

function BackupCodes() {
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  async function make(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: failed } = await authClient.twoFactor.generateBackupCodes({ password });
    setBusy(false);
    if (failed) return setError(authErrorMessage(failed));
    setCodes(data?.backupCodes ?? []);
    setAsking(false);
    setPassword('');
  }

  return (
    <Panel
      title={
        <span className="inline-flex items-center gap-2">
          Two-factor{' '}
          <Badge tone="success" dot>
            On
          </Badge>
        </span>
      }
      description="Signing in asks for a code from your authenticator app as well as your password. Developer accounts keep two-factor on."
      actions={
        <Button size="sm" onClick={() => setAsking(true)}>
          Make new backup codes
        </Button>
      }
    >
      {codes ? (
        <div className="flex flex-col gap-3">
          <Notice tone="success" title="Your new backup codes">
            Save them somewhere safe. Each works once if you lose your phone; your old codes no
            longer work.
          </Notice>
          <ul className="m-0 grid list-none grid-cols-2 gap-2 rounded-[6px] bg-field p-4 font-mono text-[13px]">
            {codes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}>
              Copy codes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCodes(null)}>
              I’ve saved them
            </Button>
          </div>
        </div>
      ) : (
        <p className="m-0 text-[13px] text-muted">
          Backup codes let you sign in without your phone. Making new ones replaces the old ones.
        </p>
      )}
      <Dialog
        open={asking}
        title="Make new backup codes"
        onClose={() => setAsking(false)}
        footer={
          <>
            <Button onClick={() => setAsking(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="backup-codes" loading={busy}>
              Make codes
            </Button>
          </>
        }
      >
        <form id="backup-codes" onSubmit={make} className="flex flex-col gap-3">
          <p className="m-0">Enter your password to make new codes.</p>
          {error && <Notice tone="danger">{error}</Notice>}
          <Field label="Password" htmlFor="codes-password">
            <Input
              id="codes-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
        </form>
      </Dialog>
    </Panel>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const { error } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (error) return setMessage({ tone: 'danger', text: authErrorMessage(error) });
    setCurrent('');
    setNext('');
    setMessage({
      tone: 'success',
      text: 'Password changed. Other browsers and CLI sessions are signed out.',
    });
  }

  return (
    <Panel
      title="Password"
      description="Changing it signs you out everywhere else, CLI sessions too."
      footer={
        <>
          <a href={`${PLAYER_SITE}/forgot-password`} className="text-[13px]">
            Forgot password
          </a>
          <Button variant="primary" size="sm" type="submit" form="change-password" loading={busy}>
            Change password
          </Button>
        </>
      }
    >
      <form id="change-password" onSubmit={submit} className="flex max-w-[420px] flex-col gap-4">
        {message && <Notice tone={message.tone}>{message.text}</Notice>}
        <Field label="Current password" htmlFor="current-password">
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>
        <Field label="New password" htmlFor="new-password" hint="At least 8 characters.">
          <Input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>
      </form>
    </Panel>
  );
}

interface BrowserSession {
  token: string;
  userAgent?: string | null;
  updatedAt: string | Date;
}

/** “Chrome on Windows” from a user agent, or a plain fallback. */
function browserName(agent: string | null | undefined): string {
  if (!agent) return 'A browser';
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Firefox\//.test(agent)
      ? 'Firefox'
      : /Chrome\//.test(agent)
        ? 'Chrome'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'A browser';
  const system = /Windows/.test(agent)
    ? 'Windows'
    : /iPhone|iPad/.test(agent)
      ? 'iPhone or iPad'
      : /Android/.test(agent)
        ? 'Android'
        : /Mac OS X/.test(agent)
          ? 'macOS'
          : /Linux/.test(agent)
            ? 'Linux'
            : null;
  return system ? `${browser} on ${system}` : browser;
}

function SignedInBrowsers() {
  const [sessions, setSessions] = useState<BrowserSession[] | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [list, session] = await Promise.all([authClient.listSessions(), authClient.getSession()]);
    if (list.error) return setError(authErrorMessage(list.error));
    setSessions((list.data ?? []) as BrowserSession[]);
    setCurrent(session.data?.session.token ?? null);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function signOutOthers() {
    setBusy(true);
    const { error: failed } = await authClient.revokeOtherSessions();
    setBusy(false);
    if (failed) return setError(authErrorMessage(failed));
    await load();
  }

  const others = (sessions ?? []).filter((session) => session.token !== current);
  return (
    <Panel
      title="Browsers signed in"
      description="Where your account is open on mogzo.com, this dashboard and the admin site."
      flush
      actions={
        others.length > 0 && (
          <Button size="sm" variant="danger" onClick={signOutOthers} loading={busy}>
            Sign out the others
          </Button>
        )
      }
    >
      {error && (
        <div className="p-5">
          <Notice tone="danger">{error}</Notice>
        </div>
      )}
      {!sessions && !error && (
        <div className="flex flex-col gap-3 p-5">
          <Skeleton width="40%" />
          <Skeleton width="30%" />
        </div>
      )}
      {sessions && (
        <table className="mz-table">
          <thead>
            <tr>
              <th scope="col">Browser</th>
              <th scope="col">Last active</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.token}>
                <td>
                  <span className="mz-cell-main">{browserName(session.userAgent)}</span>{' '}
                  {session.token === current && <Badge tone="info">This browser</Badge>}
                </td>
                <td>{formatDate(String(session.updatedAt))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function Appearance() {
  const [theme, setTheme] = useTheme();
  return (
    <Panel
      title="Theme"
      description="Kept in this browser. “Same as this computer” follows your system’s light or dark setting."
    >
      <SegmentedControl<ThemeChoice>
        label="Theme"
        value={theme}
        onChange={setTheme}
        items={(['system', 'light', 'dark'] as const).map((id) => ({ id, label: THEME_NAMES[id] }))}
      />
    </Panel>
  );
}

const SECTIONS = [
  { path: '', label: 'Profile' },
  { path: 'security', label: 'Security' },
  { path: 'appearance', label: 'Appearance' },
] as const;

/** The account inside the dashboard (M25), so the sidebar never goes away. */
export function Account() {
  const me = useDeveloper();
  const { section = '' } = useParams();
  const current = SECTIONS.find((s) => s.path === section) ?? SECTIONS[0];
  return (
    <Page
      title={me.displayName}
      crumbs={[{ label: 'Account', to: '/account' }]}
      crumb={current.label}
      leading={<Avatar name={me.displayName} size={48} />}
      meta={
        <>
          <span>{me.email}</span>
          <Badge>Developer</Badge>
        </>
      }
      tabs={
        <Tabs label="Account pages">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.path}
              to={s.path ? `/account/${s.path}` : '/account'}
              end
              className="mz-tab"
            >
              {s.label}
            </NavLink>
          ))}
        </Tabs>
      }
    >
      <div className="flex max-w-[880px] flex-col gap-6">
        {current.path === '' && <Profile />}
        {current.path === 'security' && (
          <>
            <BackupCodes />
            <ChangePassword />
            <SignedInBrowsers />
          </>
        )}
        {current.path === 'appearance' && <Appearance />}
      </div>
    </Page>
  );
}
